import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Fuel, Droplets } from "lucide-react";

const emptyFuelLog = {
  vehicle_id: "", vehicle_name: "", driver_id: "", driver_name: "",
  fuel_type: "diesel", quantity_liters: 0, cost_per_liter: 0,
  total_cost: 0, odometer_reading: 0, station_name: "", date: "", notes: ""
};

export default function FuelLogsPage() {
  const [logs, setLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyFuelLog });

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/fuel-logs`),
      axios.get(`${API}/vehicles`),
      axios.get(`${API}/drivers`)
    ]).then(([logsRes, vRes, dRes]) => {
      setLogs(logsRes.data);
      setVehicles(vRes.data);
      setDrivers(dRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const updateCost = (qty, rate) => {
    const q = Number(qty) || 0;
    const r = Number(rate) || 0;
    return { quantity_liters: q, cost_per_liter: r, total_cost: q * r };
  };

  const handleSave = async () => {
    if (!form.vehicle_id) { toast.error("Select a vehicle"); return; }
    try {
      if (editing) { await axios.put(`${API}/fuel-logs/${editing.id}`, form); toast.success("Updated"); }
      else { await axios.post(`${API}/fuel-logs`, form); toast.success("Fuel log added"); }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyFuelLog }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this fuel log?")) return;
    try { await axios.delete(`${API}/fuel-logs/${id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }
  };

  const openEdit = (l) => { setEditing(l); setForm({ ...l }); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyFuelLog }); setDialogOpen(true); };

  const totalFuelCost = logs.reduce((sum, l) => sum + (l.total_cost || 0), 0);
  const totalLiters = logs.reduce((sum, l) => sum + (l.quantity_liters || 0), 0);

  return (
    <div data-testid="fuel-logs-page">
      <PageHeader title="Fuel Logs" subtitle={`${logs.length} entries`}>
        <button data-testid="create-fuel-log-button" onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
          <Plus className="w-3.5 h-3.5" /> Log Fuel
        </button>
      </PageHeader>

      <div className="p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Fuel Cost</p>
            <p className="font-heading text-xl font-bold text-white" data-testid="total-fuel-cost">${totalFuelCost.toLocaleString()}</p>
          </div>
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Liters</p>
            <p className="font-heading text-xl font-bold text-white" data-testid="total-liters">{totalLiters.toLocaleString()} L</p>
          </div>
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Avg Cost/Liter</p>
            <p className="font-heading text-xl font-bold text-white" data-testid="avg-fuel-cost">${totalLiters > 0 ? (totalFuelCost / totalLiters).toFixed(2) : "0.00"}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-20">
            <Fuel className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No fuel logs yet.</p>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-900/50 border-b border-zinc-800">
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Vehicle</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Driver</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Liters</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">$/Liter</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Total</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Station</th>
                  <th className="text-right px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20" data-testid={`fuel-row-${l.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{l.date || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-200">{l.vehicle_name || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-400">{l.driver_name || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-300">{l.quantity_liters} L</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">${l.cost_per_liter}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-orange-400 font-medium">${(l.total_cost || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs">{l.station_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-fuel-${l.id}`} onClick={() => openEdit(l)} className="text-zinc-500 hover:text-zinc-200 p-1 mr-1"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-fuel-${l.id}`} onClick={() => handleDelete(l.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">{editing ? "Edit Fuel Log" : "Log Fuel"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vehicle *</Label>
                <select data-testid="fuel-vehicle-select" value={form.vehicle_id} onChange={e => {
                  const v = vehicles.find(vh => vh.id === e.target.value);
                  setForm({ ...form, vehicle_id: e.target.value, vehicle_name: v ? `${v.registration_number} (${v.make} ${v.model})` : "" });
                }} className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="">Select vehicle</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number} - {v.make} {v.model}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Driver</Label>
                <select data-testid="fuel-driver-select" value={form.driver_id} onChange={e => {
                  const d = drivers.find(dr => dr.id === e.target.value);
                  setForm({ ...form, driver_id: e.target.value, driver_name: d?.name || "" });
                }} className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="">Select driver</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Liters</Label>
                <Input data-testid="fuel-liters-input" type="number" value={form.quantity_liters} onChange={e => {
                  const costs = updateCost(e.target.value, form.cost_per_liter);
                  setForm({ ...form, ...costs });
                }} className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">$/Liter</Label>
                <Input data-testid="fuel-rate-input" type="number" step="0.01" value={form.cost_per_liter} onChange={e => {
                  const costs = updateCost(form.quantity_liters, e.target.value);
                  setForm({ ...form, ...costs });
                }} className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Total</Label>
                <Input data-testid="fuel-total-display" type="number" value={form.total_cost} readOnly
                  className="mt-1 bg-zinc-950 border-zinc-700 text-orange-400 rounded-[2px] font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date</Label>
                <Input data-testid="fuel-date-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Odometer (km)</Label>
                <Input data-testid="fuel-odometer-input" type="number" value={form.odometer_reading} onChange={e => setForm({ ...form, odometer_reading: Number(e.target.value) })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fuel Type</Label>
                <select data-testid="fuel-type-select" value={form.fuel_type} onChange={e => setForm({ ...form, fuel_type: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="diesel">Diesel</option>
                  <option value="petrol">Petrol</option>
                  <option value="cng">CNG</option>
                  <option value="electric">Electric</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Station</Label>
                <Input data-testid="fuel-station-input" value={form.station_name} onChange={e => setForm({ ...form, station_name: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
              <Textarea data-testid="fuel-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-fuel-log" onClick={handleSave} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Save"} Log
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
